import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { BadgeCheck, LayoutList, Megaphone, Share2, MessageCircle, CalendarDays, PhoneCall, TrendingUp, GraduationCap, Heart, Users, ChartBarIncreasing, HeartHandshakeIcon, CheckCircle } from 'lucide-react';

const BENEFIT_ICONS = {
  1: BadgeCheck,
  2: LayoutList,
  3: Megaphone,
  4: Share2,
  5: MessageCircle,
  6: CalendarDays,
  7: PhoneCall,
  8: TrendingUp,
  9: GraduationCap,
  10: Heart,
};

const BENEFIT_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const LogoHighlight = () => (
  <img
    src="https://res.cloudinary.com/dommpgy1l/image/upload/v1784401650/somosmas_logo_horizontal_nlqydy.png"
    alt="SOMOS+"
    className="h-14 w-auto"
  />
);

const ProveedoresModal = ({ onClose }) => {
  const { t } = useTranslation();
  const closeBtnRef = useRef(null);

  // Lock body scroll (iOS-correct: position fixed + scroll restoration)
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Initial focus on close button
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="proveedores-modal-title"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          ref={closeBtnRef}
          onClick={onClose}
          aria-label={t('landing.proveedores-modal.close')}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5751B] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="p-8 sm:p-10">
          <p className="text-xs font-bold uppercase tracking-widest text-[#F5751B] mb-1">
            {t('landing.proveedores-modal.label')}
          </p>
          <h2
            id="proveedores-modal-title"
            className="text-sm sm:text-xl font-extrabold text-gray-900 mb-3 leading-tight flex items-center gap-2 flex-wrap"
          >
            <Trans
              i18nKey="landing.proveedores-modal.heading"
            />
          </h2>

          <p className="text-4xl leading-7 text-gray-600 mb-6">
            <LogoHighlight />
          </p>

          
          <div className="w-[10%] h-0.5 bg-[#F5751B] rounded-full mb-4" />

          <div className="flex flex-col sm:flex-row gap-6 mb-8">
            <div className="flex-1">
              <p className="text-sm leading-7 text-gray-600 mb-3">
                {t('landing.proveedores-modal.intro-1')}
              </p>
              <p className="text-sm leading-7 text-gray-600">
                {t('landing.proveedores-modal.intro-2')}
              </p>
            </div>
            <div className="sm:w-40 shrink-0 relative flex items-center">
              <img
                src="https://res.cloudinary.com/dommpgy1l/image/upload/v1784369118/mockup_laptop_mobile_xttbn6.png"
                alt=""
                className="w-full h-auto"
              />
              <div className="absolute -bottom-3 -right-3 w-20 h-20 rounded-full bg-[#F5751B] shadow-lg flex flex-col items-center justify-center text-center px-2">
                <span className="text-[10px] font-semibold text-white/70 leading-tight">
                  {t('landing.proveedores-modal.badge-line-1')}
                </span>
                <span className="text-[10px] font-extrabold text-white leading-tight mt-0.5">
                  {t('landing.proveedores-modal.badge-line-2')}
                </span>
              </div>
            </div>
          </div>

          <div className="pt-8 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1 text-center uppercase">
              {t('landing.proveedores-modal.benefits-heading')}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {BENEFIT_KEYS.map(n => {
                const Icon = BENEFIT_ICONS[n];
                return (
                  <div key={n} className="rounded-xl bg-white shadow-sm p-3 flex flex-col items-center text-center">
                    <Icon className="text-[#F5751B] mb-2" size={20} aria-hidden="true" />
                    <p className="text-[10px] font-bold text-gray-900 mb-1 leading-tight">
                      {t(`landing.proveedores-modal.benefit-${n}-title`)}
                    </p>
                    <p className="text-[10px] text-gray-500 leading-relaxed">
                      {t(`landing.proveedores-modal.benefit-${n}-desc`)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>


          <div className="flex flex-col sm:flex-row gap-6 mb-8">
          <div className="rounded-2xl bg-[#F5751B] text-white p-6 mb-8">
            <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">
              {t('landing.proveedores-modal.price-label')}
            </p>
            <div className="flex items-end gap-1 mb-1">
              <span className="text-sm font-semibold opacity-80">
                {t('landing.proveedores-modal.price-period')}
              </span>
              <span className="text-5xl font-extrabold leading-none">
                {t('landing.proveedores-modal.price-amount')}
              </span>
            </div>
            <p className="text-sm opacity-90 mb-4">
              {t('landing.proveedores-modal.price-note')}
            </p>
            <p className="text-xs leading-6 bg-white/15 border border-white/30 rounded-lg px-3 py-2">
              {t('landing.proveedores-modal.price-freeze')}
            </p>
            <p className="text-sm font-bold mt-2 flex items-center gap-1.5">
              <CheckCircle size={15} aria-hidden="true" />
              {t('landing.proveedores-modal.price-promise')}
            </p>
          </div>

          <div className="border-t border-gray-100 pt-6 mb-8">
            <h3 className="text-xs font-bold text-gray-900 mb-3 uppercase">
              {t('landing.proveedores-modal.impact-heading')}
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              {[
                { n: 1, Icon: ChartBarIncreasing },
                { n: 2, Icon: Users },
                { n: 3, Icon: HeartHandshakeIcon },
              ].map(({ n, Icon }) => (
                <li key={n} className="flex items-start gap-2">
                  <Icon className="shrink-0 mt-0.5 text-[#F5751B]" size={16} aria-hidden="true" />
                  {t(`landing.proveedores-modal.impact-${n}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>


          <div className="rounded-2xl bg-gray-50 border border-gray-100 p-6">
            <h3 className="text-xs font-extrabold text-gray-900 uppercase tracking-wide mb-2">
              {t('landing.proveedores-modal.cta-heading')}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {t('landing.proveedores-modal.cta-desc')}
            </p>
            <a
              href="mailto:hello@somosmas.ch"
              className="inline-block rounded-md bg-[#F5751B] px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#d4610e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5751B] transition-colors"
            >
              hello@somosmas.ch
            </a>
            <div className="aspect-video w-full mt-4">
              <iframe className="w-full h-full rounded-lg" src="https://www.youtube.com/embed/hmkUAHi_WQY?si=rG3HcAC8qsEzBhD5" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProveedoresModal;
